import { PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef } from 'react';
import { remainingAttachmentsAfterSend, settleDraftAttachmentSend } from '../../../../draftMessages.store';
import { Button } from 'react-daisyui';
import useChatActions from '../../../../../../Chat.actions.ts';
import { useChatContext } from '../../../../../../Chat.hook.ts';
import { useMessageBarContext } from '../../../../MessageBar.hook.ts';
import {
  attachmentSendAlert,
  sendAttachmentsSequentially,
} from '../../../../MessageBar.utils.ts';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from 'src/lib/logging';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import { useWapiAnalytics } from 'src/hooks/analytics/useWapiAnalytics.hook';
import { WAPI_EVENTS } from 'src/hooks/analytics/wapiAnalytics.constants';
import { eChannelType } from '../../../../../../../../providers/SocketsProvider/Sockets.enums';
import { useToastContext } from '../../../../../../../../providers/ToastProvider/Toast.hook.ts';
import { useTranslation } from 'react-i18next';
import { useErrorHandler } from 'src/hooks/useErrorHandler.hook';

/**
 * Los tres rechazos del gate de saldo de whatsapp-business-service
 * (`InternalErrorMapper.go` 4036/4037/4038), ya registrados en
 * `ErrorDirectory` por RET-2103 con título y copy propios.
 *
 * Se nombran aquí por una sola razón: son los únicos donde el reintento tiene
 * sentido DESPUÉS de una acción del operador (recargar, o esperar a que el
 * wallet vuelva), así que borrarle lo que escribió lo obliga a redactarlo otra
 * vez para poder reintentar. En los demás fallos se conserva el barrido de
 * siempre.
 */
const WALLET_REFUSAL_CODES = new Set<number | string>([
  'wba-10101',
  'wba-10102',
  'wba-10103',
]);

export default function SendAction() {
  const {
    resendIds,
    blockTextArea,
    setBlockTextArea,
    isComposerWalletBlocked,
    showAllowTypesBanner,
    setReplyMessage,
    textAreaRef,
    selectedChat,
  } = useChatContext();
  const selectedLeadIdRef = useRef(selectedChat?.leadId);
  selectedLeadIdRef.current = selectedChat?.leadId;
  const { sendTextMessage, sendFileMessage, sendDriveFile } = useChatActions();
  const {
    messageBarValue,
    chatAttachments,
    setChatAttachments,
    setMessageBarValue,
    clearComposerKeepingDraft,
    setDraftMessages,
    lastSuggestedResponse,
    setLastSuggestedResponse,
    isSendingRef,
    composerSubmitAdapter,
  } = useMessageBarContext();
  const { trackEvent } = useAnalytics();
  const { trackWapiEvent } = useWapiAnalytics();
  const { addAlert } = useToastContext();
  const { handleError } = useErrorHandler();
  // `errors/errors` holds the copy for an attachment send the server refused.
  const { t } = useTranslation(['chat/chat', 'errors/errors']);

  // When a composer action is active, composerSubmitAdapter is non-null
  // and the paper-airplane doubles as that composer's submit button (no
  // duplicate "Enviar" controls). The adapter owns submit / isValid /
  // isSending; we dispatch through it.
  const textArea = textAreaRef.current;

  async function sendMessageOnClick() {
    // RET-2232 D-16: hard-gate the actual send on the wallet's own verdict,
    // read from a field ONLY the wallet composer effect writes
    // (`isComposerWalletBlocked`) — not on `blockTextArea`, which six other
    // writers (DriveFilesAction, GoogleMapsAction, TemplateActions, this same
    // component's own busy toggle, ...) clear in their `finally` without
    // knowing the balance is still short. `disabled` below is the visual
    // fixation; this is what actually stops the network call.
    if (isComposerWalletBlocked) return;

    // Composer path: delegate to the adapter's submit. The adapter owns
    // payload construction (header/footer/action fields that aren't in
    // the plain text-send flow). adapter.isSending guards double-clicks
    // without polluting isSendingRef (scoped to text sends).
    if (composerSubmitAdapter) {
      if (!composerSubmitAdapter.isValid || composerSubmitAdapter.isSending) {
        return;
      }
      await composerSubmitAdapter.submit();
      return;
    }

    if (isSendingRef.current) return;
    isSendingRef.current = true;

    const capturedLeadId = selectedChat?.leadId;

    try {
      setBlockTextArea(true);
      const isWapiChat =
        selectedChat?.integration === eChannelType.WHATSAPP_API;

      if (messageBarValue.trim() && !chatAttachments.length) {
        const textResult = await sendTextMessage(messageBarValue);
        // RET-2349 (R4): a plan-blocked send is not a send. Keep the text,
        // draft and reply, and report nothing.
        if (textResult?.blockedByPlan) return;
        // `sent === false`: the send failed or the read-only guard refused it,
        // so no "sent" event. A missing result keeps the old behaviour.
        const textWasSent = textResult?.sent !== false;
        if (textWasSent) {
          if (isWapiChat) {
            trackWapiEvent(WAPI_EVENTS.MESSAGE_TEXT_SENT, {
              leadId: selectedChat?.leadId ?? '',
              conversationId: selectedChat?.id ?? '',
              channel_type: selectedChat?.integration ?? '',
              messageType: 'text',
              messageLength: messageBarValue.length,
              hasMedia: false,
            });
          } else {
            // Non-WAPI channels (WhatsApp Web / Messenger / IG / TikTok) had no
            // send truth event at all — the core product action was invisible.
            trackEvent('front.chat.messages.text.sent.action', {
              leadId: selectedChat?.leadId ?? '',
              conversationId: selectedChat?.id ?? '',
              channel_type: selectedChat?.integration ?? '',
              messageType: 'text',
              messageLength: messageBarValue.length,
              hasMedia: false,
            });
          }
        }
      }

      if (chatAttachments.length) {
        const attachmentsResult = await sendAttachmentsSequentially({
          chatAttachments,
          messageBarValue,
          sendFileMessage,
          sendDriveFile,
          callerMethod: 'SendAction.sendMessageOnClick',
        });
        const { failedAttachments } = attachmentsResult;
        // RET-2349 (R4): nothing was tried; the producer toast already told why.
        const blockedByPlan = attachmentsResult.blockedByPlan === true;

        if (blockedByPlan) {
          // No sent/failed event: the plan refused before any send.
        } else if (failedAttachments.length < chatAttachments.length) {
          // Media sends had no truth event on ANY channel (the wapi
          // attachment.*.open events are click-level) — fire for all,
          // channel_type distinguishes WAPI.
          trackEvent('front.chat.messages.media.sent.action', {
            leadId: selectedChat?.leadId ?? '',
            conversationId: selectedChat?.id ?? '',
            channel_type: selectedChat?.integration ?? '',
            messageType: 'media',
            attachmentsCount: chatAttachments.length,
            failedCount: failedAttachments.length,
            hasMedia: true,
          });
        } else {
          // ALL attachments failed — mirror the text failure contract so
          // total media failure isn't invisible.
          trackEvent('front.chat.messages.media.failed.error', {
            leadId: selectedChat?.leadId ?? '',
            conversationId: selectedChat?.id ?? '',
            channel_type: selectedChat?.integration ?? '',
            messageType: 'media',
            attachmentsCount: chatAttachments.length,
            failedCount: failedAttachments.length,
          });
        }

        const stillOnOriginalLead = selectedLeadIdRef.current === capturedLeadId;
        if (stillOnOriginalLead) {
          setChatAttachments((current) => remainingAttachmentsAfterSend(current, { attempted: chatAttachments, failed: failedAttachments }));
        }
        const attachmentsAlert = attachmentSendAlert({
          result: attachmentsResult,
          t,
        });
        if (attachmentsAlert) {
          addAlert(attachmentsAlert);
        }
        if (blockedByPlan) return;

        setDraftMessages((current) => settleDraftAttachmentSend(current, { leadId: capturedLeadId, message: messageBarValue, attempted: chatAttachments, failed: failedAttachments }));

        if (stillOnOriginalLead) {
          setMessageBarValue('');
          if (textArea) {
            textArea.style.height = '64px';
            textArea.focus();
          }
          setReplyMessage(null);
        }
      }
      // BUG-1134: sin condicionar a `currentDraft`, que se calculó antes del
      // envío y puede estar desfasado. `settleDraftAttachmentSend` ya resuelve
      // bien el caso "este lead no tiene borrador".
      setDraftMessages((current) => settleDraftAttachmentSend(current, { leadId: capturedLeadId, message: messageBarValue, attempted: [], failed: [] }));

      if (lastSuggestedResponse) {
        trackEvent('front.chat.ai.suggested_response.sent', {
          conversationId: selectedChat?.id,
          wasModified: messageBarValue !== lastSuggestedResponse.text,
        });
        if (selectedLeadIdRef.current === capturedLeadId) setLastSuggestedResponse(undefined);
      }
    } catch (err) {
      if (selectedChat?.integration === eChannelType.WHATSAPP_API) {
        trackWapiEvent(WAPI_EVENTS.MESSAGE_TEXT_FAILED, {
          leadId: selectedChat?.leadId ?? '',
          conversationId: selectedChat?.id ?? '',
          errorMessage: err instanceof Error ? err.message : String(err),
        });
      } else {
        trackEvent('front.chat.messages.text.failed.error', {
          leadId: selectedChat?.leadId ?? '',
          conversationId: selectedChat?.id ?? '',
          channel_type: selectedChat?.integration ?? '',
          errorMessage: err instanceof Error ? err.message : String(err),
        });
      }
      LLog.error(
        {
          component: LOG_COMPONENTS.CHAT,
          method: 'SendAction.component',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error sendMessageOnClick triggered text area',
        err as unknown as Error,
      );
      /*
        RET-2232 — el rechazo del servidor tiene que VERSE.

        El guardia autoritativo de este envío no es el sello del compositor: es
        `WalletSendGate` de whatsapp-business-service, que en ENFORCE bloquea
        por saldo insuficiente y es fail-CLOSED cuando no puede determinar el
        saldo (V9). El sello y la barra son una cortesía ANTES de intentar —el
        propio `ErrorDirectory` lo dice de la barra de RET-1806: avisa «antes
        de que el envío se intente siquiera»—, así que cuando el sello no pudo
        cotizar y no bloqueó, el envío llega al servidor y el servidor lo niega.

        Hasta aquí todo correcto. Lo que estaba mal es lo que pasaba después:
        este `catch` registraba el fallo, lo mandaba a analítica, BORRABA lo
        escrito y no decía nada. El operador veía desaparecer su mensaje sin
        una razón, y la copy que lo explica ya existía —RET-2103 registró
        wba-10101/10102/10103 con título y toast— sólo que la leían los dos
        caminos de PLANTILLA y no éste, que es el que este ticket vuelve
        cobrable.

        `handleError` resuelve el código contra el mismo `entries[]` y pinta.
        Sin fallback propio a propósito: el directorio ya decide qué decir
        cuando el código no está registrado (INT-1103 prefiere el `message` del
        backend antes que una genérica del llamador), y añadir una aquí lo
        pisaría en todos los demás fallos de este camino.
      */
      const errorEntry = handleError(err);
      const isWalletRefusal =
        errorEntry !== null && WALLET_REFUSAL_CODES.has(errorEntry.code);
      if (!isWalletRefusal) {
        // BUG-1134: vacía la caja pero NO el borrador. `setMessageBarValue`
        // espeja al borrador, y usarlo aquí dejaba al vendedor sin el texto de
        // un envío que falló, que es justo cuando más lo necesita.
        clearComposerKeepingDraft();
      }
      if (textArea) {
        textArea.style.height = '64px';
        textArea.focus();
      }
      setReplyMessage(null);
    } finally {
      isSendingRef.current = false;
      if (selectedChat?.leadId === capturedLeadId) {
        setBlockTextArea(false);
      }
    }
  }

  function isDisabled() {
    if (composerSubmitAdapter) {
      // In composer mode the send gate is owned by the composer (Yup
      // schema for interactive, whatever future composers enforce).
      // messageBarValue alone is insufficient — a valid body still needs
      // valid buttons/url/list for interactive.
      return !composerSubmitAdapter.isValid || composerSubmitAdapter.isSending;
    }

    if (resendIds.length > 0 || chatAttachments.length > 0) {
      return false;
    }

    return !messageBarValue.length;
  }

  useEffect(() => {
    const active = document.activeElement;
    const isUserTyping =
      active instanceof HTMLInputElement ||
      active instanceof HTMLTextAreaElement;
    if (!isUserTyping) {
      textArea?.focus();
    }
  }, [textArea]);

  return (
    <Button
      disabled={
        isDisabled() ||
        showAllowTypesBanner ||
        blockTextArea ||
        Boolean(isComposerWalletBlocked)
      }
      onClick={sendMessageOnClick}
      size="xs"
      color="primary"
      data-tour="chat-send-button"
    >
      <PaperAirplaneIcon className="MessageBar__Icon" />
    </Button>
  );
}
